import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  Building2,
  Clock,
  Download,
  FileText,
  RotateCcw,
  Users,
  Wallet,
} from 'lucide-react';

import { formatarCentavos } from '@/nucleo/dinheiro';
import { cn } from '@/nucleo/utilitarios';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { ChartContainer, type ChartConfig } from '@/ui/base/chart';
import { Label } from '@/ui/base/label';
import { MultiSelectCombobox } from '@/ui/base/multi-select-combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import {
  aplicarFiltros,
  calcularIndicadores,
  competenciasDisponiveis,
  consolidarPorDre,
  formatarCompetenciaCurta,
  formatarCompetenciaNoEixo,
  formatarDias,
  formatarDinheiroCurtoDeCentavos,
  formatarInteiro,
  formatarPercentual,
  montarSerieTemporal,
  ordenarPorCodigo,
} from './calculos';
import { carregarRelatorioSme } from './dados';
import { exportarConsolidadoPorDre, exportarSerieTemporal } from './exportar';
import type { ConsolidadoPorDre, FiltrosRelatorioSme, TipoContratado } from './tipos';

/**
 * `/sme/relatorios` — Relatórios SME, o recorte gerencial por DRE ×
 * competência que o Portal SME entrega ao gestor: alunos, valores, prazo de
 * aprovação e devoluções.
 *
 * Quem acessa: `ADMIN`, `FINANCEIRO`, `GESTOR_SME` e `GESTOR_DRE` — os quatro
 * papéis que o guard do protótipo resolve para esta rota
 * (`<ProtectedSmeOnlyRoute gestorOnly allowGestorDre>`, `App.tsx:889`). Quem
 * controla isso é o mapa de navegação (`nucleo/navegacao/mapa.ts`,
 * `PAPEIS_RELATORIOS_SME`).
 *
 * NÃO é o "Painel executivo" de Operação nem "Indicadores Mensais": aqueles
 * medem outra coisa (regime PF × PJ, série do DTP). Este responde "quanto cada
 * DRE mediu, aprovou, devolveu e recebeu no período".
 *
 * Dado simulado, pela mesma exceção deliberada das demais telas do módulo
 * financeiro (ver `../_dev/cenarioDeTela.ts`): não existe API nem banco para
 * este recorte. Ele entra por `dados.ts`, a porta única — nenhum componente
 * desta tela lê o JSON, e quando o endpoint existir só aquele arquivo muda.
 * Os números reproduzem o protótipo: as margens visíveis lá (colunas da
 * tabela, KPIs, série temporal, barras por DRE) são todas exatas; o que o
 * protótipo não mostra (como cada total se reparte entre competências e entre
 * PF/PJ) é proporcional. As competências são as cinco últimas fechadas,
 * contadas do relógio — o protótipo mostra 03/26 a 07/26 porque era o mês
 * dele; data literal no mock envelhece sozinha.
 *
 * Uma divergência conhecida, e deliberada: o protótipo mostra 44,4 d em
 * "Prazo médio de aprovação" e, ao mesmo tempo, os prazos por DRE que, pesados
 * pelas medições de cada uma, dão 45,1 d. Os dois números não podem sair da
 * mesma base — o do protótipo vem do grão de medição, que esta tela não tem.
 * Aqui o indicador é calculado a partir do que a própria tabela mostra, para
 * o topo e o quadro nunca se contradizerem.
 */

/**
 * Resolvido uma vez por carga da página, não a cada render: as competências
 * saem do relógio, e recalcular a cada render faria a tela trocar de mês
 * sozinha à meia-noite, no meio de uma demonstração.
 */
const LINHAS = carregarRelatorioSme();

const CONFIG_BARRAS: ChartConfig = {
  valor: { label: 'Valor', color: 'hsl(var(--primary))' },
};

const CONFIG_SERIE: ChartConfig = {
  valor: { label: 'Valor (R$)', color: 'hsl(var(--primary))' },
  devolucoes: { label: 'Devoluções', color: 'hsl(var(--destructive))' },
};

const ESTILO_DICA = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 8,
  fontSize: 12,
} as const;

const COLUNAS: DataTableColumn<ConsolidadoPorDre>[] = [
  {
    id: 'dre',
    header: 'DRE',
    sortKey: 'dreCodigo',
    cell: (linha) => (
      <span className="whitespace-nowrap">
        <span className="font-mono text-xs text-muted-foreground">{linha.dreCodigo}</span>{' '}
        <span className="font-medium">{linha.dreNome}</span>
      </span>
    ),
  },
  {
    header: 'Credenciados',
    sortKey: 'credenciados',
    cell: (linha) => formatarInteiro(linha.credenciados),
    headerClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
  },
  {
    header: 'Apontamentos',
    sortKey: 'apontamentos',
    cell: (linha) => formatarInteiro(linha.apontamentos),
    headerClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
  },
  {
    header: 'Devoluções',
    sortKey: 'devolucoes',
    cell: (linha) => formatarInteiro(linha.devolucoes),
    headerClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
  },
  {
    id: 'percentualDevolucao',
    header: '% Dev.',
    sortKey: (linha) => linha.percentualDevolucao,
    cell: (linha) => (
      // Vermelho só quando houve devolução: zero é o resultado bom, e pintar
      // tudo de vermelho apagaria a diferença entre as DREs.
      <span className={cn(linha.percentualDevolucao ? 'font-medium text-destructive' : undefined)}>
        {formatarPercentual(linha.percentualDevolucao)}
      </span>
    ),
    headerClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
  },
  {
    id: 'prazoMedio',
    header: 'Prazo médio',
    sortKey: (linha) => linha.prazoMedioDias,
    cell: (linha) => formatarDias(linha.prazoMedioDias),
    headerClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
    tooltip: 'Da medição enviada até aprovada, em dias — média ponderada pelas medições da DRE.',
  },
  {
    header: 'Medições',
    sortKey: 'medicoes',
    cell: (linha) => formatarInteiro(linha.medicoes),
    headerClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
  },
  {
    header: 'Pagas',
    sortKey: 'pagas',
    cell: (linha) => formatarInteiro(linha.pagas),
    headerClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
    tooltip: 'Medições já pagas ou liquidadas no período.',
  },
  {
    header: 'Alunos',
    sortKey: 'alunos',
    cell: (linha) => formatarInteiro(linha.alunos),
    headerClassName: 'text-right',
    cellClassName: 'text-right tabular-nums',
  },
  {
    id: 'valor',
    header: 'Valor total',
    sortKey: 'valorCentavos',
    cell: (linha) => <MoneyDisplay value={linha.valorCentavos / 100} />,
    headerClassName: 'text-right',
    cellClassName: 'text-right',
  },
  {
    id: 'valorPago',
    header: 'Valor pago',
    sortKey: 'valorPagoCentavos',
    cell: (linha) => <MoneyDisplay value={linha.valorPagoCentavos / 100} />,
    headerClassName: 'text-right',
    cellClassName: 'text-right',
  },
];

export function RelatoriosSmePage() {
  const cenario = useCenarioDeTela();

  const [tipo, setTipo] = useState<TipoContratado | 'TODOS'>('TODOS');
  const [competencia, setCompetencia] = useState<string>('TODOS');
  const [dreIds, setDreIds] = useState<string[]>([]);

  const competencias = useMemo(() => competenciasDisponiveis(LINHAS), []);
  const opcoesDeDre = useMemo(() => {
    const mapa = new Map<string, { label: string; code: string }>();
    for (const linha of LINHAS) {
      mapa.set(linha.dreId, { label: linha.dreNome, code: linha.dreCodigo });
    }
    return Array.from(mapa.entries())
      .map(([value, { label, code }]) => ({ value, label, code }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, []);

  const filtros: FiltrosRelatorioSme = { tipo, competencia, dreIds };
  const linhasFiltradas = useMemo(
    () => (cenario === 'sucesso' ? aplicarFiltros(LINHAS, filtros) : []),
    // `filtros` é recriado a cada render de propósito (é só um agrupamento);
    // as dependências reais são os três campos.
    [cenario, tipo, competencia, dreIds],
  );

  const indicadores = useMemo(() => calcularIndicadores(linhasFiltradas), [linhasFiltradas]);
  const consolidado = useMemo(() => consolidarPorDre(linhasFiltradas), [linhasFiltradas]);
  const serie = useMemo(() => montarSerieTemporal(linhasFiltradas), [linhasFiltradas]);
  const barras = useMemo(
    () =>
      ordenarPorCodigo(consolidado).map((dre) => ({
        dre: dre.dreCodigo,
        dreNome: dre.dreNome,
        valor: dre.valorCentavos / 100,
      })),
    [consolidado],
  );
  const pontosDaSerie = useMemo(
    () =>
      serie.map((ponto) => ({
        mes: formatarCompetenciaNoEixo(ponto.competencia),
        valor: ponto.valorCentavos / 100,
        devolucoes: ponto.devolucoes,
      })),
    [serie],
  );

  const indicadoresDoTopo: StatItem[] = [
    {
      title: 'Valor total (período)',
      value: formatarCentavos(indicadores.valorCentavos),
      description: `Pago/liquidado: ${formatarCentavos(indicadores.valorPagoCentavos)}`,
      icon: Wallet,
    },
    {
      title: 'Medições no período',
      value: formatarInteiro(indicadores.medicoes),
      description: `${formatarInteiro(indicadores.alunos)} alunos transportados`,
      icon: FileText,
    },
    {
      title: 'Devoluções de apontamentos',
      value: formatarInteiro(indicadores.devolucoes),
      description: `${formatarPercentual(indicadores.percentualDevolucao)} do total`,
      icon: RotateCcw,
    },
    {
      title: 'Prazo médio de aprovação',
      value: formatarDias(indicadores.prazoMedioDias),
      description: 'Da medição enviada até aprovada',
      icon: Clock,
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Relatórios SME"
        description="Visão gerencial por DRE × competência: alunos, valores, prazos e devoluções."
      />

      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-3">
          <div>
            <Label htmlFor="relatorios-sme-tipo">Tipo</Label>
            <Select value={tipo} onValueChange={(v) => setTipo(v as TipoContratado | 'TODOS')}>
              <SelectTrigger id="relatorios-sme-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="PJ">PJ (pessoa jurídica)</SelectItem>
                <SelectItem value="PF">CL (pessoa física)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="relatorios-sme-periodo">Período</Label>
            <Select value={competencia} onValueChange={setCompetencia}>
              <SelectTrigger id="relatorios-sme-periodo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                {competencias.map((mes) => (
                  <SelectItem key={mes} value={mes}>
                    {formatarCompetenciaCurta(mes)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="relatorios-sme-dres">DREs</Label>
            <MultiSelectCombobox
              options={opcoesDeDre}
              selected={dreIds}
              onChange={setDreIds}
              placeholder="Todas as DREs"
              searchPlaceholder="Buscar por código ou nome..."
              renderSelectedText={(n) => `${n} DRE${n === 1 ? '' : 's'}`}
              ariaLabel="DREs"
              triggerClassName="w-full"
            />
          </div>
        </CardContent>
      </Card>

      <StatsGrid stats={indicadoresDoTopo} columns="grid-cols-1 sm:grid-cols-2 xl:grid-cols-4" />

      <EstadoSimulado
        cenario={cenario}
        descricao="os relatórios da SME"
        colunasDoEsqueleto={COLUNAS.length}
      >
        {consolidado.length > 0 && (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="min-w-0">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="h-4 w-4 text-primary" aria-hidden="true" />
                  Valor total por DRE no período
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={CONFIG_BARRAS}
                  className="aspect-auto h-[300px] w-full"
                  role="img"
                  aria-label="Valor total por DRE no período selecionado"
                >
                  <BarChart data={barras} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid
                      vertical={false}
                      strokeDasharray="3 3"
                      stroke="hsl(var(--muted-foreground))"
                      strokeOpacity={0.4}
                    />
                    <XAxis
                      dataKey="dre"
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      width={64}
                      tickFormatter={(v: number) => formatarDinheiroCurtoDeCentavos(v * 100)}
                    />
                    <Tooltip
                      cursor={{ fill: 'hsl(var(--muted) / 0.3)' }}
                      contentStyle={ESTILO_DICA}
                      formatter={(valor: number) => [formatarCentavos(valor * 100), 'Valor']}
                      labelFormatter={(_rotulo, carga) => {
                        const ponto = carga?.[0]?.payload as
                          { dre: string; dreNome: string } | undefined;
                        return ponto ? `${ponto.dre} — ${ponto.dreNome}` : '';
                      }}
                    />
                    <Bar
                      dataKey="valor"
                      fill="var(--color-valor)"
                      radius={[4, 4, 0, 0]}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="h-4 w-4 text-primary" aria-hidden="true" />
                  Série temporal — valor e devoluções
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={CONFIG_SERIE}
                  className="aspect-auto h-[300px] w-full"
                  role="img"
                  aria-label="Valor e devoluções por competência no período selecionado"
                >
                  <LineChart data={pontosDaSerie} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--muted-foreground))"
                      strokeOpacity={0.4}
                    />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="valor"
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      width={64}
                      tickFormatter={(v: number) => formatarDinheiroCurtoDeCentavos(v * 100)}
                    />
                    <YAxis
                      yAxisId="devolucoes"
                      orientation="right"
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      width={40}
                    />
                    <Tooltip
                      cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' }}
                      contentStyle={ESTILO_DICA}
                      formatter={(valor: number, nome: string) =>
                        nome === 'valor'
                          ? [formatarCentavos(valor * 100), 'Valor (R$)']
                          : [formatarInteiro(valor), 'Devoluções']
                      }
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11 }}
                      formatter={(v) => (v === 'valor' ? 'Valor (R$)' : 'Devoluções')}
                    />
                    <Line
                      yAxisId="valor"
                      type="monotone"
                      dataKey="valor"
                      name="valor"
                      stroke="var(--color-valor)"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      isAnimationActive={false}
                    />
                    <Line
                      yAxisId="devolucoes"
                      type="monotone"
                      dataKey="devolucoes"
                      name="devolucoes"
                      stroke="var(--color-devolucoes)"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="-mb-4 rounded-b-none border-b-0">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="rounded-xl bg-orange/10 p-2">
                <Users className="h-4 w-4 text-orange/80" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-base font-semibold">Quadro consolidado por DRE</h2>
                <p className="text-xs text-muted-foreground">
                  {consolidado.length} DRE(s) no período selecionado
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={serie.length === 0}
                onClick={() => exportarSerieTemporal(serie)}
              >
                <Download className="mr-1 h-4 w-4" aria-hidden="true" /> Série temporal
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={consolidado.length === 0}
                onClick={() => exportarConsolidadoPorDre(consolidado)}
              >
                <Download className="mr-1 h-4 w-4" aria-hidden="true" /> Por DRE
              </Button>
            </div>
          </CardContent>
        </Card>

        <DataTable
          columns={COLUNAS}
          data={consolidado}
          minWidth="1080px"
          ariaLabel="Quadro consolidado por DRE"
          emptyIcon={Users}
          emptyTitle="Nenhuma DRE no período"
          emptyDescription="Ajuste o tipo, o período ou as DREs selecionadas. Pode ser que as medições da competência ainda não tenham sido enviadas."
        />
      </EstadoSimulado>
    </div>
  );
}

export default RelatoriosSmePage;
